import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import { useConnectionState, useToastState } from '../../store/useAppStore';

interface ChatErrorBoundaryProps {
  children: React.ReactNode;
}

const ChatErrorBoundary: React.FC<ChatErrorBoundaryProps> = ({ children }) => {
  const { isConnected } = useConnectionState();
  const { showToast } = useToastState();

  const handleChatError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log specific chat error
    console.error('Chat Error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      isConnected,
      timestamp: new Date().toISOString()
    });

    // Show specific toast for chat errors
    showToast({
      type: 'error',
      title: 'Erro no Chat',
      message: 'Ocorreu um erro no sistema de chat. Tente reconectar ou recarregar a página.',
      duration: 8000,
      action: {
        label: 'Reconectar',
        onClick: () => window.location.reload()
      }
    });
  };

  const ChatErrorFallback = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      padding: '2rem',
      textAlign: 'center',
      backgroundColor: '#fef2f2',
      borderRadius: '12px',
      margin: '1rem',
      border: '2px solid #fecaca'
    }}>
      <div style={{
        fontSize: '3rem',
        marginBottom: '1rem'
      }}>
        💬❌
      </div>
      
      <h3 style={{
        fontSize: '1.25rem',
        fontWeight: 'bold',
        color: '#dc2626',
        marginBottom: '0.5rem'
      }}>
        Erro no Sistema de Chat
      </h3>
      
      <p style={{
        color: '#7f1d1d',
        marginBottom: '1.5rem',
        maxWidth: '300px'
      }}>
        O sistema de chat encontrou um problema. Verifique sua conexão e tente novamente.
      </p>

      <div style={{
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          🔄 Reconectar
        </button>

        <button
          onClick={() => window.location.href = '/'}
          style={{
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          🏠 Ir para Início
        </button>
      </div>

      {!isConnected && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: '#fee2e2',
          borderRadius: '6px',
          fontSize: '0.875rem',
          color: '#991b1b'
        }}>
          ⚠️ WhatsApp desconectado. Reconecte para usar o chat.
        </div>
      )}
    </div>
  );

  return (
    <ErrorBoundary onError={handleChatError} fallback={<ChatErrorFallback />}>
      {children}
    </ErrorBoundary>
  );
};

export default ChatErrorBoundary;